import { useCallback, useEffect, useState } from 'react'
import type { ChartResult, ChartSpec } from '../types/chart'
import { runChartQuery } from '../services/chartQueryService'

export interface UseChartOptions {
  workspaceId: string
  chartId?: string
  spec?: ChartSpec
  uid?: string
  enabled?: boolean
}

export interface UseChartReturn {
  result: ChartResult | null
  loading: boolean
  error: Error | null
  refresh: () => Promise<ChartResult | null>
}

interface CacheEntry {
  result: ChartResult
  timestamp: number
}

const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes
const clientChartCache = new Map<string, CacheEntry>()

export function clearClientChartCacheForTests(): void {
  clientChartCache.clear()
}

function hashString(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(16)
}

function buildClientCacheKey(workspaceId: string, specOrId: ChartSpec | string | undefined, uid: string): string {
  const specPart = typeof specOrId === 'string'
    ? `id:${specOrId}`
    : `spec:${hashString(JSON.stringify(specOrId ?? {}))}`
  return `${workspaceId}:${specPart}:u:${uid}`
}

/**
 * useChart — React hook for executing and caching chart queries.
 * 5-minute client cache keyed by (spec/chartId hash + uid).
 * Never imports firebase/* directly.
 */
export function useChart({
  workspaceId,
  chartId,
  spec,
  uid = 'anon',
  enabled = true,
}: UseChartOptions): UseChartReturn {
  const cacheKey = buildClientCacheKey(workspaceId, spec ?? chartId, uid)

  const [result, setResult] = useState<ChartResult | null>(() => {
    const cached = clientChartCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CLIENT_CACHE_TTL_MS) {
      return cached.result
    }
    return null
  })
  const [error, setError] = useState<Error | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [prevKey, setPrevKey] = useState(cacheKey)

  if (cacheKey !== prevKey) {
    setPrevKey(cacheKey)
    const cached = clientChartCache.get(cacheKey)
    setResult(cached ? cached.result : null)
    setError(null)
  }

  const loading = refreshing || Boolean(enabled && workspaceId && (chartId || spec) && !result && !error)

  useEffect(() => {
    if (!enabled || !workspaceId || (!chartId && !spec)) {
      return
    }

    let isCurrent = true
    const cached = clientChartCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CLIENT_CACHE_TTL_MS) {
      return
    }

    runChartQuery({
      workspaceId,
      chartId,
      spec,
      refresh: false,
    })
      .then((queryResult) => {
        if (!isCurrent) return
        clientChartCache.set(cacheKey, {
          result: queryResult,
          timestamp: Date.now(),
        })
        setResult(queryResult)
      })
      .catch((err) => {
        if (!isCurrent) return
        setError(err instanceof Error ? err : new Error(String(err)))
      })

    return () => {
      isCurrent = false
    }
  }, [cacheKey, enabled, workspaceId, chartId, spec])

  const refresh = useCallback(async () => {
    if (!workspaceId || (!chartId && !spec)) {
      return null
    }

    setRefreshing(true)
    setError(null)

    try {
      const queryResult = await runChartQuery({
        workspaceId,
        chartId,
        spec,
        refresh: true,
      })

      clientChartCache.set(cacheKey, {
        result: queryResult,
        timestamp: Date.now(),
      })
      setResult(queryResult)
      setRefreshing(false)
      return queryResult
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error(String(err))
      setError(errorObj)
      setRefreshing(false)
      throw errorObj
    }
  }, [workspaceId, chartId, spec, cacheKey])

  return {
    result,
    loading,
    error,
    refresh,
  }
}
